import * as SwitchPrimitive from "@radix-ui/react-switch";
import * as React from "react";

import { cn } from "@/web/lib/styling";

/** Solid fill; unchecked dimming is opacity on the track layer (smooth) vs swapping bg + /30 (harsh / no duration). */
const trackFill = {
  blue: "bg-withus-blue",
  yellow: "bg-withus-yellow",
  red: "bg-withus-red",
  default: "bg-black dark:bg-black",
} as const;

const trackEase = "ease-[cubic-bezier(0.25,0.46,0.45,0.94)]";

export function Switch({
  className,
  color,
  ...props
}: React.ComponentProps<typeof SwitchPrimitive.Root> & {
  color?: "blue" | "yellow" | "red";
}) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      className={cn(
        "group peer relative inline-flex h-[22px] w-[38px] shrink-0 cursor-pointer items-center rounded-none",
        "transition-colors duration-200 outline-none",
        "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
        "aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20",
        "dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40",
        "after:absolute after:-inset-x-3 after:-inset-y-2",
        "data-disabled:cursor-not-allowed data-disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 z-0 transition-opacity duration-200",
          trackEase,
          "opacity-30 group-data-[state=checked]:opacity-100",
          color ? trackFill[color] : trackFill.default,
        )}
      />
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          "pointer-events-none relative z-10 block rounded-none ring-0",
          "ml-[3px] h-[16px] w-[16px]",
          "translate-x-0 transition-transform duration-200",
          trackEase,
          "data-[state=checked]:translate-x-[16px]",
          "bg-background dark:data-[state=checked]:bg-primary-foreground dark:data-[state=unchecked]:bg-foreground",
        )}
      />
    </SwitchPrimitive.Root>
  );
}
